import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getListAccountingTypesQueryKey,
  getListMastersFlatQueryKey,
  type UpdateKanMutationError,
  type UpdateKoMutationError,
  type UpdateMokuMutationError,
  useUpdateKan,
  useUpdateKo,
  useUpdateMoku,
} from '@/entities/masters/api/generated/api';
import { toast } from '@/shared/ui';

export const useExpenditureItemsEditMutation = ({ onSuccess }: { onSuccess: () => void }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const invalidateAndNotify = () => {
    toast.success(
      t('common.messages.update-success', {
        name: t('master-management.labels.expenditure-items'),
      }),
    );
    queryClient.invalidateQueries({
      queryKey: getListMastersFlatQueryKey(),
    });
    queryClient.invalidateQueries({
      queryKey: getListAccountingTypesQueryKey(),
    });
    onSuccess();
  };

  const handleError = (error: { response?: { data: { error_code?: string } } }) => {
    if (error.response?.data.error_code === 'VALIDATION_ERROR') {
      toast.error(t('master-management.expenditure-items.error.duplicate-code'));
      return;
    }
    toast.error(
      t('common.messages.update-failed', {
        name: t('master-management.labels.expenditure-items'),
      }),
    );
  };

  const kanMutation = useUpdateKan({
    request: { suppressToast: true },
    mutation: {
      onSuccess: invalidateAndNotify,
      onError: (error: UpdateKanMutationError) => handleError(error),
    },
  });

  const koMutation = useUpdateKo({
    request: { suppressToast: true },
    mutation: {
      onSuccess: invalidateAndNotify,
      onError: (error: UpdateKoMutationError) => handleError(error),
    },
  });

  const mokuMutation = useUpdateMoku({
    request: { suppressToast: true },
    mutation: {
      onSuccess: invalidateAndNotify,
      onError: (error: UpdateMokuMutationError) => handleError(error),
    },
  });

  return {
    kanMutation,
    koMutation,
    mokuMutation,
    isPending: kanMutation.isPending || koMutation.isPending || mokuMutation.isPending,
  };
};
